import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import { CardActionArea, Container, Box, Fade, Zoom } from "@mui/material";
import audioReleaseImg from "../../assets/img/audio_release.jpg";
import videoReleaseImg from "../../assets/img/video_release.jpg";
import Grid from "@mui/material/Grid";
import { useHistory } from "react-router-dom";
import * as actions from "../Releases/CreateReleases/actions";
import { toastError } from "../../utils";
import noaccess from "../../assets/img/noaccess.png";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import AlbumIcon from "@mui/icons-material/Album";
import MicIcon from "@mui/icons-material/Mic";
import VideocamIcon from "@mui/icons-material/Videocam";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AwayStatusModal from "../../components/Common/AwayStatusModal";
import SEO from "../../components/SEO";

// Styled components for modern design
const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 400,
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 24px 48px rgba(26, 115, 232, 0.25), 0 12px 24px rgba(0, 191, 165, 0.15), 0 0 1px rgba(0, 0, 0, 0.1)',
    '& .card-icon': {
      transform: 'scale(1.1) rotate(5deg)',
    },
    '& .card-overlay': {
      opacity: 1,
    },
  },
}));

const CardOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  opacity: 0,
  transition: 'opacity 0.4s ease',
  pointerEvents: 'none',
});

const IconWrapper = styled(Box)({
  position: 'absolute',
  top: '16px',
  right: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '12px',
  padding: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease',
  zIndex: 1,
});

const PageHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '24px',
  padding: '0 16px',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '32px',
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '40px',
  },
  '& h1': {
    fontSize: '1.5rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '8px',
    fontFamily: "'DM Sans', sans-serif",
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.75rem',
      marginBottom: '10px',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '2.25rem',
      marginBottom: '12px',
    },
  },
  '& p': {
    fontSize: '0.875rem',
    color: '#5f6368',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: 1.6,
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
      fontSize: '0.95rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.0rem',
    },
  },
}));

const MaintenanceBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '20px',
  padding: '48px 32px',
  textAlign: 'center',
  color: 'white',
  boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)',
  '& p': {
    fontSize: '1.25rem',
    lineHeight: 1.8,
    margin: 0,
  },
}));

const RevokedBox = styled(Box)(({ theme }) => ({
  background: 'white',
  borderRadius: '24px',
  padding: '56px 40px',
  textAlign: 'center',
  boxShadow: '0 20px 60px rgba(211, 47, 47, 0.15), 0 8px 24px rgba(0, 0, 0, 0.1)',
  border: '3px solid rgba(244, 67, 54, 0.2)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'radial-gradient(circle, rgba(244, 67, 54, 0.03) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  '& img': {
    maxWidth: '220px',
    marginBottom: '32px',
    filter: 'drop-shadow(0 4px 12px rgba(211, 47, 47, 0.2))',
    opacity: 0.95,
  },
  '& .title': {
    fontSize: '2rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #f44336 0%, #c62828 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '8px',
    fontFamily: "'DM Sans', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& .subtitle': {
    fontSize: '0.875rem',
    color: '#d32f2f',
    fontWeight: 600,
    marginBottom: '24px',
    fontFamily: "'DM Sans', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '1px',
    background: 'rgba(244, 67, 54, 0.08)',
    padding: '8px 16px',
    borderRadius: '8px',
    display: 'inline-block',
  },
  '& .message': {
    fontSize: '1.125rem',
    color: '#424242',
    lineHeight: 1.8,
    marginBottom: '32px',
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 500,
  },
  '& .signature': {
    fontSize: '1rem',
    color: '#616161',
    fontWeight: 600,
    textAlign: 'right',
    fontFamily: "'DM Sans', sans-serif",
    fontStyle: 'normal',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '40px 24px',
    '& .title': {
      fontSize: '1.5rem',
    },
    '& .message': {
      fontSize: '1rem',
    },
  },
}));

const StyledCardContent = styled(CardContent)({
  padding: '24px',
  textAlign: 'center',
  '& .MuiTypography-h5': {
    fontWeight: 600,
    color: '#202124',
    marginBottom: '12px',
    fontFamily: "'DM Sans', sans-serif",
  },
  '& .MuiTypography-body2': {
    color: '#5f6368',
    lineHeight: 1.6,
    fontSize: '0.95rem',
  },
});

const ArrowIcon = styled(ArrowForwardIcon)({
  marginLeft: '8px',
  opacity: 0,
  transition: 'all 0.3s ease',
});

function ReleaseCheck(props) {
  const history = useHistory();
  const [awayData, setAwayData] = useState({
    status: false,
    text: "",
  });
  const [isUserRevoked, setIsUserRevoked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    props.setLoader(true);
    actions.checkAwayStatus().then((res) => {
      const isAway =
        (res &&
          res.body &&
          res.body.status &&
          res.body.status === "yes" &&
          true) ||
        false;
      const awayText =
        (res && res.body && res.body.text && res.body.text) || "";
      if (isAway) {
        setAwayData({
          status: true,
          text: awayText,
        });
        props.setLoader(false);
        setIsLoading(false);
      } else {
        actions
          .checkUserRevokeStatus()
          .then((res) => {
            if (res.revoked === 1) {
              setIsUserRevoked(true);
            }
          })
          .catch((err) => {
            toastError(err.message);
          })
          .finally(() => {
            props.setLoader(false);
            setIsLoading(false);
          });
      }
    });
  }, []);

  return (
    <>
    <SEO
      title="Select Release Type"
      description="Choose between audio or video release to start distributing your music content across all major platforms."
    />
    <Box
      sx={{
        minHeight: '90vh',
        height: 'auto',
        background: 'linear-gradient(135deg, #fafbfc 0%, #f0f2f5 100%)',
        position: 'relative',
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: '16px 8px', sm: '24px 16px' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(26, 115, 232, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(0, 191, 165, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(26, 115, 232, 0.06) 0%, transparent 40%)
          `,
          pointerEvents: 'none',
        },
        '@keyframes gentle-float': {
          '0%, 100%': {
            transform: 'translateY(0px)',
          },
          '50%': {
            transform: 'translateY(-10px)',
          },
        },
        '@keyframes gentle-float-slow': {
          '0%, 100%': {
            transform: 'translateY(0px)',
          },
          '50%': {
            transform: 'translateY(-12px)',
          },
        },
        '@keyframes rotate-slow': {
          '0%': {
            transform: 'rotate(0deg)',
          },
          '100%': {
            transform: 'rotate(360deg)',
          },
        },
      }}
    >
      {/* Decorative audio icons */}
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          right: '10%',
          color: 'rgba(26, 115, 232, 0.15)',
          pointerEvents: 'none',
          animation: 'gentle-float 6s ease-in-out infinite',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <HeadphonesIcon sx={{ fontSize: 72 }} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '25%',
          left: '8%',
          color: 'rgba(0, 191, 165, 0.15)',
          pointerEvents: 'none',
          animation: 'gentle-float-slow 8s ease-in-out infinite',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <MicIcon sx={{ fontSize: 68 }} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '12%',
          color: 'rgba(26, 115, 232, 0.13)',
          pointerEvents: 'none',
          animation: 'gentle-float 7s ease-in-out infinite',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <MusicNoteIcon sx={{ fontSize: 70 }} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: '15%',
          color: 'rgba(0, 191, 165, 0.13)',
          pointerEvents: 'none',
          animation: 'rotate-slow 20s linear infinite',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <AlbumIcon sx={{ fontSize: 75 }} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: '30%',
          right: '10%',
          color: 'rgba(26, 115, 232, 0.15)',
          pointerEvents: 'none',
          animation: 'gentle-float-slow 9s ease-in-out infinite',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <VideocamIcon sx={{ fontSize: 70 }} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '35%',
          left: '10%',
          color: 'rgba(0, 191, 165, 0.14)',
          pointerEvents: 'none',
          animation: 'gentle-float 8s ease-in-out infinite',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <PlayCircleOutlineIcon sx={{ fontSize: 72 }} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          right: '8%',
          color: 'rgba(26, 115, 232, 0.12)',
          pointerEvents: 'none',
          animation: 'gentle-float-slow 7s ease-in-out infinite',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <VideoLibraryIcon sx={{ fontSize: 68 }} />
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 0 }, position: 'relative', zIndex: 1, width: '100%', display: 'flex', alignItems: 'center' }}>
        {awayData.status && !isLoading && (
        <Fade in timeout={800}>
          <Box sx={{ width: '100%' }}>
            <AwayStatusModal awayText={awayData.text} />
          </Box>
        </Fade>
      )}

      {isUserRevoked && !isLoading && (
        <Fade in timeout={800}>
          <Grid container>
            <Grid item xs={12} sm={10} md={8} lg={6} sx={{ margin: "auto" }}>
              <RevokedBox>
                <img src={noaccess} alt="Access Denied" />
                <Typography className="title">Access Restricted</Typography>
                <Typography className="subtitle">Terms Violation</Typography>
                <Typography className="message">
                  You cannot create a release due to the violation of Fronic Media
                  distribution terms. To regain access, please contact{" "}
                  <a
                    href="mailto:support@fronicmedia.com"
                    style={{
                      color: '#d32f2f',
                      textDecoration: 'none',
                      fontWeight: 700,
                      borderBottom: '2px solid rgba(211, 47, 47, 0.4)',
                      paddingBottom: '2px',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#c62828';
                      e.target.style.borderBottomColor = '#c62828';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#d32f2f';
                      e.target.style.borderBottomColor = 'rgba(211, 47, 47, 0.4)';
                    }}
                  >
                    support@fronicmedia.com
                  </a>
                  .
                </Typography>
                <Typography className="signature">
                  - Team Fronic Media
                </Typography>
              </RevokedBox>
            </Grid>
          </Grid>
        </Fade>
      )}

      {!awayData.status && !isUserRevoked && !isLoading && (
        <Fade in timeout={1000}>
          <Box sx={{ width: '100%' }}>
            <PageHeader>
              <Typography variant="h1">Choose Your Release</Typography>
              <Typography>
                Choose the type of release you want to distribute across music platforms worldwide.
              </Typography>
            </PageHeader>

            <Grid
              container
              spacing={4}
              justifyContent="center"
              alignItems="stretch"
              sx={{ maxWidth: '1000px', margin: '0 auto', gap: 4 }}
            >
              <Grid item xs={12} sm={6} md={5} lg={5} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Zoom in timeout={600} style={{ width: '100%' }}>
                  <StyledCard onClick={() => history.push("/create_releases")} sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <IconWrapper className="card-icon">
                        <MusicNoteIcon sx={{ fontSize: 32, color: '#1a73e8' }} />
                      </IconWrapper>
                      <CardActionArea sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%', flexGrow: 1 }}>
                        <CardMedia
                          component="img"
                          height="280"
                          image={audioReleaseImg}
                          alt="Audio Release"
                          sx={{
                            objectFit: 'cover',
                            filter: 'brightness(0.95)',
                          }}
                        />
                        <CardOverlay className="card-overlay" />
                        <StyledCardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography gutterBottom variant="h5" component="div">
                            Audio Release
                          </Typography>
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            Distribute your music as Single, EP, Album, or Compilation
                            to streaming platforms worldwide.
                          </Typography>
                        </StyledCardContent>
                      </CardActionArea>
                    </Box>
                  </StyledCard>
                </Zoom>
              </Grid>

              <Grid item xs={12} sm={6} md={5} lg={5} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Zoom in timeout={800} style={{ width: '100%' }}>
                  <StyledCard onClick={() => history.push("/create_video_releases")} sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <IconWrapper className="card-icon">
                        <VideoLibraryIcon sx={{ fontSize: 32, color: '#00bfa5' }} />
                      </IconWrapper>
                      <CardActionArea sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%', flexGrow: 1 }}>
                        <CardMedia
                          component="img"
                          height="280"
                          image={videoReleaseImg}
                          alt="Video Release"
                          sx={{
                            objectFit: 'cover',
                            filter: 'brightness(0.95)',
                          }}
                        />
                        <CardOverlay className="card-overlay" />
                        <StyledCardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography gutterBottom variant="h5" component="div">
                            Video Release
                          </Typography>
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            Distribute your music videos to platforms worldwide and
                            expand your visual presence.
                          </Typography>
                        </StyledCardContent>
                      </CardActionArea>
                    </Box>
                  </StyledCard>
                </Zoom>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}
      </Container>
    </Box>
    </>
  );
}

export default ReleaseCheck;
